/**
 * Day divider for chat (Mission 20 unified – web, iOS, Android).
 * Renders a date label between two lines. Used when the first message of a new day appears.
 * Stays in the chat forever; derived at render time from message createdAt.
 */

import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { CoachCedarColors, Spacing } from '@/utils/styleConverter';

export interface DayDividerProps {
  /** Date key for the day (YYYY-MM-DD) */
  dateKey: string;
  /** Optional label; if not provided, derived from dateKey in local locale (e.g. "Mon 3 Jun") */
  label?: string;
}

function formatDayLabel(dateKey: string): string {
  try {
    const [y, m, d] = dateKey.split('-').map(Number);
    const date = new Date(y, m - 1, d);

    // Check if dateKey is today
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (dateKey === todayKey) return 'Today';

    // Check if dateKey is yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    if (dateKey === yesterdayKey) return 'Yesterday';

    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  } catch {
    return dateKey;
  }
}

export function DayDivider({ dateKey, label }: DayDividerProps) {
  const displayLabel = label ?? formatDayLabel(dateKey);

  const lineEl = Platform.OS === 'web' ? (
    <div style={styles.lineGroup as React.CSSProperties}>
      <div style={styles.lineBlack as React.CSSProperties} />
      <div style={styles.lineGreen as React.CSSProperties} />
    </div>
  ) : (
    <View style={styles.lineGroup}>
      <View style={styles.lineBlack} />
      <View style={styles.lineGreen} />
    </View>
  );

  if (Platform.OS === 'web') {
    return (
      <div className="day-divider-wrapper" style={styles.webWrapper as React.CSSProperties}>
        {lineEl}
        <span style={styles.label as React.CSSProperties}>{displayLabel}</span>
        {lineEl}
      </div>
    );
  }

  return (
    <View style={styles.wrapper}>
      {lineEl}
      <Text style={styles.label}>{displayLabel}</Text>
      {lineEl}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingVertical: Spacing.md,
    paddingHorizontal: 0,
    gap: 8,
  },
  webWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingTop: Spacing.md,
    paddingBottom: Spacing.md,
    paddingLeft: 0,
    paddingRight: 0,
    gap: 8,
  },
  lineGroup: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    gap: 2,
  },
  lineBlack: {
    height: 1,
    width: '100%',
    backgroundColor: '#000',
    opacity: 0.3,
  },
  lineGreen: {
    height: 1,
    width: '100%',
    backgroundColor: CoachCedarColors.primary,
    opacity: 0.9,
  },
  label: {
    color: CoachCedarColors.primary,
    fontSize: 12,
    fontWeight: '500',
    paddingHorizontal: 12,
    flexShrink: 0,
  },
});
